import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import ProjectService from "@app/projects/projectService";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { getServicingGranularityHeader } from "@app/projects/network-assessment/adequacy/utility";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces/zipCodeDisplayModel";
import { ZipClassAggregateResultDisplayModel } from "@app/projects/zip-projects/interfaces/zipClassAggregateResultDisplayModel";
import ZipProjectService from "@app/projects/zipProjectService";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { AdequacyDetail } from "@app/projects/network-assessment/adequacy/adequacyDetail";
import { PreviewWaffleToppings, simulationPreviewWaffleToppings, zipSimulationPreviewWaffleToppings } from "@app/projects/servicing-provider/SimulationPreviewHelpers";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ColumnConfig, ColumnGroup } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { UserInfo } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

interface SimulateProviderTerminationProps {
  providerNetworkType: ProviderNetworkTypeEnum;
  regionSetId: number;
  selectedProviders: ServicingProvider[];
  specialtyGroupCode: string;
  user: UserInfo;
  identifier: string;
  allLocations: boolean;
  allSpecialties: boolean;
  isZipProject: boolean;
  hasTerminationError?: boolean;
}

const SimulateProviderTermination: FC<React.PropsWithChildren<SimulateProviderTerminationProps>> = (props: SimulateProviderTerminationProps) => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<AdequacyDetail[]>([]);
  const [zipCodePreview, setZipCodePreview] = useState<ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>([]);
  const [zipClassPreview, setZipClassPreview] = useState<ZipClassAggregateResultDisplayModel[]>([]);
  const projectService = useRef<ProjectService>(new ProjectService()).current;
  const zipProjectService = useRef<ZipProjectService>(new ZipProjectService()).current;
  const [error, setError] = useState<JSX.Element>(null);
  const projectContext = useContext(CountyProjectContext);
  const zipProjectContext = useContext(ZipProjectContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const [previouslyTerminatedProvidersCount, setPreviouslyTerminatedProvidersCount] = useState<number>(null);
  const [controlsChanged, setControlsChanged] = useState<boolean>(false)
  const [allSpecialties, setAllSpecialties] = useState<boolean>(props.allSpecialties);
  const [allLocations, setAllLocations] = useState(props.allLocations);
  const [identifier, setIdentifier] = useState(props.identifier);
  const tinViewer = props.user.roles.includes(Roles.TIN_VIEWER);

  const cmsCreditIsApplying = props.isZipProject ? false : projectContext.cmsCreditIsApplying;
  const runConfiguration = props.isZipProject ? zipProjectContext.state.runConfiguration : projectContext.runConfiguration;
  const networkAssessmentAnalysisType = props.isZipProject ? NetworkAssessmentAnalysisType.ServiceArea : projectContext.networkAssessmentAnalysisType;
  const project = props.isZipProject ? zipProjectContext.state.project : projectContext.project;
  const templateMetadata = props.isZipProject ? zipProjectContext.state.templateMetadata : projectContext.templateMetadata;
  const { projectRunConfigurationType } = runConfiguration;

  const adequacyData = props.isZipProject ? null : projectContext.adequacyResult.details;
  const zipCodeData = props.isZipProject ? zipProjectContext.state.zipDetails : null;
  const zipClassData = props.isZipProject ? zipProjectContext.state.zipClassDrillInDetails : null;


  useEffect(() => {
    //Before simulating, validate that all providers have NPI or TIN as appropriate, based on TIN_Viewer role
    validateProviderTypes(props.selectedProviders, tinViewer, !props.isZipProject && identifier.includes("name"));

    if (!props.isZipProject) {
      projectService.simulateTermination(
        project.projectId,
        props.regionSetId,
        {
          allLocations: false,
          allSpecialties: false,
          providerIds: props.selectedProviders.map((sp) => sp.providerId),
          providerIdToContractEntityId: props.selectedProviders.map((provider) => {
            return {
              contractEntityId: provider.professionalAffiliationId,
              providerId: provider.providerId
            }
          }),
          providerNetworkType: props.providerNetworkType,
          terminateByContractEntity: identifier.includes("contractentity"),
          terminateByNPI: identifier.includes("npi"),
          terminateByTIN: identifier.includes("tin") && tinViewer,
        },
        props.specialtyGroupCode,
        projectRunConfigurationType,
        networkAssessmentAnalysisType
      ).then((result) => {
        setPreview([result]);
        setPreviouslyTerminatedProvidersCount(result.previouslyTerminatedProvidersCount);
        setLoading(false);
        setControlsChanged(false);
      }, () => {
        setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
      });
    }
    else {
      zipProjectService.simulateZipCodeTermination(
        project.projectId,
        props.regionSetId,
        props.specialtyGroupCode,
        {
          allLocations: props.allLocations,
          allSpecialties: props.allSpecialties,
          providerIds: props.selectedProviders.map((sp) => sp.providerId),
          providerNetworkType: props.providerNetworkType,
          terminateByNPI: identifier.includes("npi"),
          terminateByTIN: identifier.includes("tin") && tinViewer,
        },
        networkAssessmentAnalysisType,
        projectRunConfigurationType
      ).then((result) => {
        setZipCodePreview([result]);
        setZipClassPreview(result.zipClassPreview ? [result.zipClassPreview] : []);
        setPreviouslyTerminatedProvidersCount(result.previouslyTerminatedProviderCount);
        setLoading(false);
        setControlsChanged(false);
      }, () => {
        setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
        setLoading(false)
      });
    }
  }, []);

  useEffect(() => {
    if (props.allLocations != allLocations || props.allSpecialties != allSpecialties || props.identifier != identifier)
      setControlsChanged(true);

    setAllLocations(props.allLocations);
    setAllSpecialties(props.allSpecialties);
    setIdentifier(props.identifier);

  }, [props.allLocations, props.allSpecialties, props.identifier]);

  const terminationPreviewWaffleToppings: PreviewWaffleToppings[] = useMemo(() => {
    if (!props.isZipProject)
      return simulationPreviewWaffleToppings(preview, adequacyData, cmsCreditIsApplying);
    else
      return zipSimulationPreviewWaffleToppings(zipCodePreview, zipCodeData, zipProjectContext.state.calculationMethod);
  }, [preview, adequacyData, zipCodePreview, zipCodeData]);

  const terminationAggregateWaffleToppings: PreviewWaffleToppings[] = useMemo(() => {
    return zipSimulationPreviewWaffleToppings(zipClassPreview, zipClassData, zipProjectContext.state.calculationMethod);
  }, [zipClassPreview, zipClassData]);

  const validateProviderTypes = (selectedProviders: ServicingProvider[], hasTINViewerRole: boolean, terminatingByName: boolean): void => {
    if (terminatingByName) {
      return;
    }

    let hasNPIOnly = false;
    let hasTINOnly = false;
    let allHaveNPI = true;

    for (let i = 0; i < selectedProviders.length; i++) {
      if (selectedProviders[i].npi === undefined) {
        allHaveNPI = false;

        if (selectedProviders[i].tin !== undefined) {
          hasTINOnly = true;
        }
      } else if (selectedProviders[i].npi !== undefined && selectedProviders[i].tin === undefined) {
        hasNPIOnly = true;
      }
    }

    if (hasTINViewerRole && hasNPIOnly && hasTINOnly) {
      // Throw an error if there are providers with only NPIs and providers with only TINs.
      setError(<QESAlert type="danger">All selected providers must have NPIs or all selected providers must have TINs.</QESAlert>);
    } else if (!hasTINViewerRole && !allHaveNPI) {
      // Throw an error if there are providers with only NPIs and providers with only TINs.
      setError(<QESAlert type="danger">All selected providers must have NPIs.</QESAlert>);
    }
  };

  const columnConfig: ColumnConfig = useMemo(() => {
    const showWithTimeAndDistanceColumns = runConfiguration.showWithTimeAndDistanceColumns;

    return {
      accessMet: {
        displayText: "Met",
        hidden: showWithTimeAndDistanceColumns || props.isZipProject,
        noSort: true,
        testId: "access-met",
      },
      accessMetDist: {
        displayText: "Met Dist",
        hidden: !showWithTimeAndDistanceColumns || props.isZipProject,
        noSort: true,
        testId: "access-met-dist",
      },
      accessMetTime: {
        displayText: "Met Time",
        hidden: !showWithTimeAndDistanceColumns || props.isZipProject,
        noSort: true,
        testId: "access-met-time",
      },
      accessWith: {
        displayText: "With %",
        noSort: true,
        testId: "access-with-percent",
      },
      accessWithout: {
        displayText: "Without %",
        noSort: true,
        testId: "access-without-percent",
      },
      overallGapAge: {
        displayText: "Gap Age",
        hidden: !project.includeGapAging || (props.isZipProject && !enabledFeatureFlags.has(Features.ZIP_GAP_AGE_TRACKING)),
        noSort: true,
        testId: "overall-gap-age",
      },
      overallMet: {
        displayText: "Met",
        noSort: true,
        testId: "overall-met",
      },
      servicingDistinct: {
        displayText: "Servicing",
        hidden: props.isZipProject,
        noSort: true,
        testId: "servicing-distinct",
      },
      servicingMet: {
        displayText: "Met",
        hidden: props.isZipProject,
        noSort: true,
        testId: "servicing-met",
      },
      servicingProviderCount: {
        displayText: getServicingGranularityHeader(templateMetadata.servicingAnalysisGranularity),
        hidden: props.isZipProject,
        noSort: true,
        testId: "servicing-provider-count",
      },
      servicingRequired: {
        displayText: "Required",
        hidden: props.isZipProject,
        noSort: true,
        testId: "servicing-required",
      },
    };
  }, [runConfiguration, project, templateMetadata, enabledFeatureFlags]);

  const columnGroups: Array<ColumnGroup<PreviewWaffleToppings>> = [
    {
      children: ["accessWith", "accessWithout", "accessMetDist", "accessMetTime", "accessMet"],
      hideKey: false,
      key: "Access Requirements",
      testId: "group-access-requirements",
    },
    {
      children: ["servicingRequired", "servicingProviderCount", "servicingDistinct", "servicingMet"],
      key: "Servicing Provider Requirements",
      testId: "group-servicing-requirements",
    },
    {
      children: ["overallMet", "overallGapAge"],
      key: "Overall",
      testId: "group-overall",
    },
  ];

  const rowDecorator = (record: PreviewWaffleToppings) => {
    if (record.overallMet === "Y") {
      return `success`;
    } else if (record.accessMet === "Y" || record.servicingMet === "Y") {
      return `warning`;
    } else {
      return `danger`;
    }
  };

  const handleReloadRequest = () => {
    setLoading(true);
    setControlsChanged(false);
    setPreview([]);

    if (!props.isZipProject) {
      projectService.simulateTermination(
        project.projectId,
        props.regionSetId,
        {
          allLocations: allLocations,
          allSpecialties: allSpecialties,
          providerIds: props.selectedProviders.map((sp) => sp.providerId),
          providerIdToContractEntityId: props.selectedProviders.map((provider) => {
            return {
              contractEntityId: provider.professionalAffiliationId,
              providerId: provider.providerId
            }
          }),
          providerNetworkType: props.providerNetworkType,
          terminateByContractEntity: identifier.includes("contractentity"),
          terminateByNPI: identifier.includes("npi"),
          terminateByTIN: identifier.includes("tin") && tinViewer
        },
        props.specialtyGroupCode,
        projectRunConfigurationType,
        networkAssessmentAnalysisType
      ).then((result) => {
        setPreview([result]);
        setPreviouslyTerminatedProvidersCount(result.previouslyTerminatedProvidersCount);
        setLoading(false);
      }, () => {
        setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
      });
    }
    else {
      zipProjectService.simulateZipCodeTermination(
        project.projectId,
        props.regionSetId,
        props.specialtyGroupCode,
        {
          allLocations: allLocations,
          allSpecialties: allSpecialties,
          providerIds: props.selectedProviders.map((sp) => sp.providerId),
          providerNetworkType: props.providerNetworkType,
          terminateByNPI: identifier.includes("npi"),
          terminateByTIN: identifier.includes("tin") && tinViewer,
        },
        networkAssessmentAnalysisType,
        projectRunConfigurationType
      ).then((result) => {
        setZipCodePreview([result]);
        setZipClassPreview(result.zipClassPreview ? [result.zipClassPreview] : []);
        setPreviouslyTerminatedProvidersCount(result.previouslyTerminatedProviderCount);
        setLoading(false);
      }, () => {
        setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
      });
    }
  }

  const renderLoadedContent = (selectedProviders: ServicingProvider[]) => {
    return (
      <>
        <div className="mb-3">
          <p className="mb-0">The top row is what this {props.isZipProject ? "zip code/specialty" : "county/specialty"} combination looks like without termination.</p>
          {previouslyTerminatedProvidersCount === 0 &&
          <p>The bottom row is what it looks like with the terminated providers listed below.</p>}
          {previouslyTerminatedProvidersCount > 0 &&
          <QESAlert type="info" persist={true}>
            <div>
              The bottom row is what your adequacy results would look like if the terminated provider(s) listed below and {previouslyTerminatedProvidersCount} other provider(s) who have been terminated previously were removed from your network. For full visibility for terminated providers, we recommend running the following report:
              <ul>
                <li>Network Terminated Provider Report</li>
              </ul>
            </div>
          </QESAlert>}
          {props.isZipProject && <h4 className="header-s" >Impact Results By Zip Code</h4>}
          {renderTable(terminationPreviewWaffleToppings)}
          {props.isZipProject && <h4 className="header-s" style={{ marginTop: "0.5rem" }}>Impact Results By Classification</h4>}
          {props.isZipProject && <QESAlert type="warning" persist={true}>This classification reflects the selected ZIP code only and may differ from the final result, which is calculated across all ZIP codes associated with the provider.</QESAlert>}
          {props.isZipProject && renderTable(terminationAggregateWaffleToppings, true)}
          <div className= "mt-3">
            <QESButton qesType="secondary" 
                       qesTestId="simulate-terminations__reload_simulation" 
                       onClick={handleReloadRequest} 
                       disabled={!controlsChanged || props.hasTerminationError}>Rerun Termination Preview</QESButton>
          </div>
        </div>
        <div className="mt-3 mb-5">
          <AccordionContainer>
            <Accordion
              title={<>{<QesIcon isDecorative iconName={FontAwesome.UserMd} width="11" height="14" className="align-baseline me-1" style={{ position: "relative", top: "1px" }} />}Selected Providers</>}
              defaultOpen={true}
              testId="simulate-provider-termination__selected-providers-accordion"
            >
              <ul>
              {selectedProviders.map((provider) => <li key={provider.providerId}>
                {provider.name} | {provider.npi ? provider.npi + " | " : ""}{provider.address} {provider.city}, {provider.state} {provider.zip}
              </li>)}
              </ul>
            </Accordion>
          </AccordionContainer>
        </div>
      </>
    );
  };

  // ZIP classifications have no gap age.
  const renderTable = (tableData: PreviewWaffleToppings[], hideGapAge: boolean = false) => {
    const columns: ColumnConfig = hideGapAge ? { ...columnConfig, overallGapAge: { ...columnConfig.overallGapAge, hidden: true } } : columnConfig;
    return (
      <Waffles<PreviewWaffleToppings>
        columns={columns}
        columnGroups={columnGroups}
        rowDecorator={rowDecorator}
        loading={loading}
        data={tableData}
        testId="simulate-provider-termination"
      />
    );
  };

  return (
    <div>
      <div className="row">
        <div className="col-12">
          {error ? error :
            !!preview ? renderLoadedContent(props.selectedProviders) : <LoadingSpinner />}
        </div>
      </div>
    </div>
  );
};

export { SimulateProviderTermination };
